import { createContext, useContext, useState } from "react";

type Theme = "dark" | "light" | "system";

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeProviderContext = createContext<ThemeProviderState | null>(null);

export function ThemeProvider({
  children,
  defaultTheme: _defaultTheme = "dark",
  storageKey: _storageKey = "compbox-ui-theme",
  ...props
}: ThemeProviderProps) {
  // App is dark-only for now — lock to "dark" and make setTheme a no-op.
  // When light/system themes are enabled, restore localStorage read + setTheme logic.
  const [theme] = useState<Theme>("dark");

  const value = {
    theme,
    setTheme: (_newTheme: Theme) => {
      // no-op while app is dark-only
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeProviderContext);

  if (context === null) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
}
